import {
  assertSafeCalendarText,
  maximumBulkCalendarPayloadBytes,
  maximumCalendarAlarmsPerEvent,
  maximumCalendarPayloadBytes,
  type BulkCalendarPayload,
  type CalendarPayload,
  type CalendarProjection,
} from "./calendar-handoff";

const encoder = new TextEncoder();
const productId = "-//ReminderTracker//Personal Calendar Handoff 1//EN";
const description =
  "This is a one-off calendar copy. Changes you make in Reminder Tracker won’t appear here automatically.";

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

export function validateCalendarDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(value);
  if (match === null) throw new RangeError("Choose a valid calendar date.");
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    year < 1900 ||
    year > 9999 ||
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    throw new RangeError("Choose a valid calendar date.");
  }
  return value;
}

export function nextCalendarDate(value: string): string {
  const valid = validateCalendarDate(value);
  const [year, month, day] = valid.split("-").map(Number) as [number, number, number];
  const next = new Date(Date.UTC(year, month - 1, day + 1));
  return `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}`;
}

function compactDate(value: string): string {
  return validateCalendarDate(value).replaceAll("-", "");
}

function utcTimestamp(epochMilliseconds: number): string {
  if (!Number.isSafeInteger(epochMilliseconds) || epochMilliseconds % 1_000 !== 0) {
    throw new RangeError("Choose a reminder time recorded to whole seconds.");
  }
  const value = new Date(epochMilliseconds);
  if (
    Number.isNaN(value.valueOf()) ||
    value.getUTCFullYear() < 1900 ||
    value.getUTCFullYear() > 9999
  ) {
    throw new RangeError("Choose a supported reminder time.");
  }
  return `${value.getUTCFullYear()}${pad(value.getUTCMonth() + 1)}${pad(value.getUTCDate())}T${pad(value.getUTCHours())}${pad(value.getUTCMinutes())}${pad(value.getUTCSeconds())}Z`;
}

function parseUtcTimestamp(value: string): number {
  const parsed = Date.parse(
    `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}T${value.slice(9, 11)}:${value.slice(11, 13)}:${value.slice(13, 15)}Z`,
  );
  if (!Number.isFinite(parsed) || utcTimestamp(parsed) !== value) {
    throw new RangeError("One reminder has an invalid calendar alert time.");
  }
  return parsed;
}

export function escapeCalendarText(value: string): string {
  const canonical = value.replaceAll("\r\n", "\n").replaceAll("\r", "\n");
  assertSafeCalendarText(canonical);
  let result = "";
  for (const character of canonical) {
    if (character === "\\") result += "\\\\";
    else if (character === ";") result += "\\;";
    else if (character === ",") result += "\\,";
    else if (character === "\n") result += "\\n";
    else result += character;
  }
  return result;
}

export function foldCalendarLine(line: string): string[] {
  assertSafeCalendarText(line);
  const output: string[] = [];
  let current = "";
  let bytes = 0;
  for (const character of line) {
    const size = encoder.encode(character).byteLength;
    const limit = output.length === 0 ? 75 : 74;
    if (bytes + size > limit) {
      output.push(current);
      current = ` ${character}`;
      bytes = 1 + size;
    } else {
      current += character;
      bytes += size;
    }
  }
  output.push(current);
  return output;
}

export function serializeCalendarEvent(projection: CalendarProjection): string {
  if (!/^cal_[A-Za-z0-9_-]{8,120}@remindertracker\.invalid$/u.test(projection.uid)) {
    throw new RangeError("Calendar identity is invalid.");
  }
  if (!Number.isSafeInteger(projection.sequence) || projection.sequence < 0) {
    throw new RangeError("Calendar sequence is invalid.");
  }
  if (projection.alarms.length > maximumCalendarAlarmsPerEvent) {
    throw new RangeError("Choose no more than 5 calendar alerts for one event.");
  }
  const eventLines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    `PRODID:${productId}`,
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${projection.uid}`,
    `SEQUENCE:${projection.sequence}`,
    `DTSTAMP:${utcTimestamp(projection.modifiedAtEpochMilliseconds)}`,
  ];
  if (projection.timing.kind === "date") {
    eventLines.push(`DTSTART;VALUE=DATE:${compactDate(projection.timing.isoDate)}`);
    eventLines.push(`DTEND;VALUE=DATE:${compactDate(nextCalendarDate(projection.timing.isoDate))}`);
  } else {
    eventLines.push(`DTSTART:${utcTimestamp(projection.timing.epochMilliseconds)}`);
  }
  eventLines.push(
    `SUMMARY:${escapeCalendarText(projection.summary)}`,
    `DESCRIPTION:${escapeCalendarText(description)}`,
    "TRANSP:TRANSPARENT",
    "CLASS:PRIVATE",
  );
  for (const alarm of projection.alarms) {
    eventLines.push(
      "BEGIN:VALARM",
      "ACTION:DISPLAY",
      `TRIGGER;VALUE=DATE-TIME:${utcTimestamp(alarm.triggerAtEpochMilliseconds)}`,
      "DESCRIPTION:Reminder alert",
      "END:VALARM",
    );
  }
  eventLines.push("END:VEVENT", "END:VCALENDAR");
  const result = `${eventLines.flatMap(foldCalendarLine).join("\r\n")}\r\n`;
  if (encoder.encode(result).byteLength > maximumCalendarPayloadBytes) {
    throw new RangeError("This calendar file is larger than the supported limit.");
  }
  return result;
}

/** Combines server-prepared events without changing their stable UID or SEQUENCE fields. */
export function mergePreparedCalendarEvents(
  payloads: ReadonlyArray<CalendarPayload>,
  exportedAtEpochMilliseconds: number,
): BulkCalendarPayload {
  if (payloads.length < 2 || payloads.length > 50) {
    throw new RangeError("Choose between 2 and 50 reminders.");
  }
  const exportTime = new Date(exportedAtEpochMilliseconds);
  if (
    !Number.isSafeInteger(exportedAtEpochMilliseconds) ||
    Number.isNaN(exportTime.valueOf()) ||
    exportTime.getUTCFullYear() < 1900 ||
    exportTime.getUTCFullYear() > 9999
  ) {
    throw new RangeError("Choose a valid calendar export time.");
  }
  const preparedEvents = payloads.flatMap((payload) => alertEventsFromPreparedPayload(payload));
  const events = preparedEvents.filter(
    (event) => event.triggerAtEpochMilliseconds >= exportedAtEpochMilliseconds,
  );
  if (events.length === 0) {
    throw new RangeError("The selected reminders have no upcoming calendar alerts.");
  }
  const utf8Text = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    `PRODID:${productId}`,
    "CALSCALE:GREGORIAN",
    ...events.map((event) => event.utf8Text),
    "END:VCALENDAR",
    "",
  ].join("\r\n");
  const byteLength = encoder.encode(utf8Text).byteLength;
  if (byteLength > maximumBulkCalendarPayloadBytes) {
    throw new RangeError("This calendar file is larger than the supported limit.");
  }
  return {
    preparationPublicId: `bulk:${payloads.map((payload) => payload.preparationPublicId).join(",")}`,
    filename: "reminders.ics",
    mediaType: "text/calendar;charset=utf-8",
    utf8Text,
    byteLength,
    expiresAt: Math.min(...payloads.map((payload) => payload.expiresAt)),
    includedEventCount: events.length,
    omittedPastEventCount: preparedEvents.length - events.length,
  };
}

type PreparedAlertEvent = Readonly<{
  triggerAtEpochMilliseconds: number;
  utf8Text: string;
}>;

function alertEventsFromPreparedPayload(payload: CalendarPayload): PreparedAlertEvent[] {
  const match =
    /^BEGIN:VCALENDAR\r\n[\s\S]*?\r\nBEGIN:VEVENT\r\n([\s\S]*?)\r\nEND:VEVENT\r\nEND:VCALENDAR\r\n$/u.exec(
      payload.utf8Text,
    );
  if (match === null) {
    throw new RangeError("One reminder could not be combined safely.");
  }
  const eventBody = match[1];
  if (eventBody === undefined) {
    throw new RangeError("One reminder could not be combined safely.");
  }
  const unfolded = eventBody.replace(/\r\n[ \t]/gu, "");
  const eventHeader = unfolded.split("\r\nBEGIN:VALARM", 1)[0] ?? "";
  const required = (name: string): string => {
    const values = eventHeader
      .split("\r\n")
      .filter((line) => line.startsWith(`${name}:`))
      .map((line) => line.slice(name.length + 1));
    if (values.length !== 1 || values[0] === undefined) {
      throw new RangeError("One reminder could not be combined safely.");
    }
    return values[0];
  };
  const sourceUid = required("UID");
  const uidMatch = /^(cal_[A-Za-z0-9_-]{8,100})@remindertracker\.invalid$/u.exec(sourceUid);
  if (uidMatch === null) {
    throw new RangeError("One reminder has an invalid calendar identity.");
  }
  const sequence = required("SEQUENCE");
  if (!/^\d+$/u.test(sequence)) {
    throw new RangeError("One reminder has an invalid calendar sequence.");
  }
  const stamp = required("DTSTAMP");
  const summary = required("SUMMARY");
  const eventDescription = required("DESCRIPTION");
  const triggers = [
    ...unfolded.matchAll(
      /BEGIN:VALARM\r\n[\s\S]*?TRIGGER;VALUE=DATE-TIME:(\d{8}T\d{6}Z)\r\n[\s\S]*?END:VALARM/gu,
    ),
  ].map((alarm) => alarm[1]);
  if (triggers.length === 0 || triggers.length > maximumCalendarAlarmsPerEvent) {
    throw new RangeError("Each selected reminder needs between 1 and 5 calendar alerts.");
  }
  return triggers.map((trigger, index) => {
    if (trigger === undefined) {
      throw new RangeError("One reminder could not be combined safely.");
    }
    const lines = [
      "BEGIN:VEVENT",
      `UID:${uidMatch[1]}_alert_${index + 1}@remindertracker.invalid`,
      `SEQUENCE:${sequence}`,
      `DTSTAMP:${stamp}`,
      `DTSTART:${trigger}`,
      `SUMMARY:${summary}`,
      `DESCRIPTION:${eventDescription}`,
      "TRANSP:TRANSPARENT",
      "CLASS:PRIVATE",
      "BEGIN:VALARM",
      "ACTION:DISPLAY",
      "TRIGGER:PT0M",
      "DESCRIPTION:Reminder alert",
      "END:VALARM",
      "END:VEVENT",
    ];
    return {
      triggerAtEpochMilliseconds: parseUtcTimestamp(trigger),
      utf8Text: lines.flatMap(foldCalendarLine).join("\r\n"),
    };
  });
}
